import { CardShellEcho, CardContent } from '../common';
import { Button, Flex, theme } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useState } from 'react';
import AdvertiserEcho from './AdvertiserEcho';
import NoDataMessageEcho from '../../states/NoDataMessageEcho';
import type { TopAdvertiser } from '@/types';
import { useAdvertisersQuery } from '@/services/revenueApi';
import { useFetchQuery, usePrefetchOnHover } from '@/hooks';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { AdvertisersDrawerEcho } from './AdvertisersDrawerEcho';

const TopAdvertisersCardEcho = ({ className }: { className?: string }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Initial query: only fetch top 5 advertisers
  const { queryResult: advertisers } = useFetchQuery(useAdvertisersQuery, undefined, { limit: 5 });
   // Prefetch query: all advertisers, disabled until user hovers over "More advertisers" button
  const { queryResult: allAdvertisers } = useFetchQuery(useAdvertisersQuery, { enabled: false });
  const handlePrefetch = usePrefetchOnHover(allAdvertisers.refetch);

  const topFiveAdvertisers = advertisers.data;
  const isDrawerEnabled = topFiveAdvertisers !== undefined && topFiveAdvertisers.length >= 5;
  const drawerData = allAdvertisers.data ?? topFiveAdvertisers ?? [];

  return (
    <CardShellEcho
      className={className}
      style={{ display: 'flex', flexDirection: 'column' }}
      title="Top advertisers"
      headerOptions={
        isDrawerEnabled ? (
          <Button
            type="text"
            color="primary"
            variant="text"
            style={{ width: 'fit-content' }}
            iconPlacement="end"
            icon={<RightOutlined />}
            onMouseEnter={handlePrefetch}
            onClick={() => {
              mixpanelTrack(MIXPANEL_EVENT.earnings.topAdvertisers.moreAdvertisersClicked, {
                widget: RevenueDashboardWidget.TopAdvertisers,
                page: 'earnings',
              });
              setIsDrawerOpen(true);
            }}
          >
            More advertisers
          </Button>
        ) : undefined
      }
    >
      <CardContent
        {...advertisers}
        renderData={(data) => <Data data={data} />}
      />
      {isDrawerEnabled && (
        <AdvertisersDrawerEcho
          isOpen={isDrawerOpen}
          handleClose={() => setIsDrawerOpen(false)}
          data={drawerData}
        />
      )}
    </CardShellEcho>
  );
};

interface DataProps {
  data: TopAdvertiser[];
}

const Data = ({ data }: DataProps) => {
  const { token } = theme.useToken();
  const topFiveAdvertisers = data;

  if (!topFiveAdvertisers.length) {
    return <NoDataMessageEcho dataType="advertisers" />;
  }

  return (
    <Flex vertical gap={token.paddingLG}>
      <Flex vertical gap={token.paddingXL} component="ul">
        {topFiveAdvertisers.map((advertiser, index: number) => (
          <li key={`${index}-${advertiser.brand}`}>
            <AdvertiserEcho data={advertiser} />
          </li>
        ))}
      </Flex>
    </Flex>
  );
};

export default TopAdvertisersCardEcho;
